import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import {
  usePreviewScenarioPurge,
  usePreviewTenantPurge,
  usePurgeScenarioRows,
  usePurgeTenantRows,
  useSimulationGenerated,
  type SimulationGeneratedRow,
  type SimulationPurgePreview,
  type SimulationPurgeSelection,
} from '@/hooks/use-platform-simulation'

function toggle(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]
}

function RowChecks({
  legend,
  rows,
  selected,
  onToggle,
}: {
  legend: string
  rows: SimulationGeneratedRow[]
  selected: string[]
  onToggle: (id: string) => void
}) {
  if (rows.length === 0) return null
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{legend}</legend>
      <ul className="space-y-1">
        {rows.map((row) => (
          <li key={row.id}>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={selected.includes(row.id)}
                onCheckedChange={() => onToggle(row.id)}
                aria-label={`Select ${row.label}`}
              />
              <span>{row.label}</span>
              {row.marked ? null : (
                <span className="text-muted-foreground">Not marked as simulation</span>
              )}
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  )
}

function PreviewDialog({
  open,
  preview,
  pending,
  title,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  preview: SimulationPurgePreview | null
  pending: boolean
  title: string
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  const count = preview?.would_delete.length ?? 0
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {count === 0
              ? 'Nothing would be deleted.'
              : `${count} simulation-marked ${count === 1 ? 'row' : 'rows'} would be deleted. This cannot be undone.`}
            {preview?.refused.length
              ? ` ${preview.refused.length} selected ${preview.refused.length === 1 ? 'row stays' : 'rows stay'}: ${preview.refused
                  .map((row) => row.reason)
                  .join('; ')}.`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            disabled={pending || count === 0}
            onClick={onConfirm}
          >
            {pending ? 'Removing…' : 'Remove'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function DataBenchRemoval({
  scenarioId,
  selectedProfileIds,
  onSelectedProfileIdsChange,
}: {
  scenarioId: string
  selectedProfileIds?: string[]
  onSelectedProfileIdsChange?: (ids: string[]) => void
}) {
  const generated = useSimulationGenerated(scenarioId)
  const preview = usePreviewScenarioPurge(scenarioId)
  const purge = usePurgeScenarioRows(scenarioId)
  const [orgIds, setOrgIds] = useState<string[]>([])
  const [jobIds, setJobIds] = useState<string[]>([])
  const [applicationIds, setApplicationIds] = useState<string[]>([])
  const [localProfileIds, setLocalProfileIds] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [plan, setPlan] = useState<SimulationPurgePreview | null>(null)

  const profileIds = selectedProfileIds ?? localProfileIds
  const setProfileIds = onSelectedProfileIdsChange ?? setLocalProfileIds
  const peopleOnList = Boolean(onSelectedProfileIdsChange)

  const selection = useMemo<SimulationPurgeSelection>(
    () => ({
      profile_ids: profileIds,
      organisation_ids: orgIds,
      opportunity_ids: jobIds,
      application_ids: applicationIds,
    }),
    [profileIds, orgIds, jobIds, applicationIds],
  )
  const selectedCount = profileIds.length + orgIds.length + jobIds.length + applicationIds.length
  const rows = generated.data

  async function onPreview() {
    try {
      const next = await preview.mutateAsync(selection)
      setPlan(next)
      setOpen(true)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not preview removal')
    }
  }

  async function onConfirm() {
    const deletable = plan?.would_delete ?? []
    if (deletable.length === 0) return
    const body: SimulationPurgeSelection = {
      profile_ids: deletable.filter((row) => row.kind === 'profile').map((row) => row.id),
      organisation_ids: deletable.filter((row) => row.kind === 'organisation').map((row) => row.id),
      opportunity_ids: deletable.filter((row) => row.kind === 'opportunity').map((row) => row.id),
      application_ids: deletable.filter((row) => row.kind === 'application').map((row) => row.id),
    }
    try {
      await purge.mutateAsync(body)
      toast.success('Simulation rows removed')
      setOpen(false)
      setPlan(null)
      setOrgIds([])
      setJobIds([])
      setApplicationIds([])
      setProfileIds([])
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not remove rows')
    }
  }

  return (
    <EntityManageSection
      title="Remove simulation rows"
      subtitle="Preview first. Only simulation-marked rows are deleted. The tenant anchor organisation stays."
    >
      <div className="space-y-4">
        {generated.isLoading ? <p className="text-sm text-muted-foreground">Loading rows…</p> : null}
        {rows ? (
          <>
            {peopleOnList ? null : (
              <RowChecks
                legend="People"
                rows={rows.profiles}
                selected={profileIds}
                onToggle={(id) => setProfileIds(toggle(profileIds, id))}
              />
            )}
            <RowChecks
              legend="Organisations"
              rows={rows.organisations}
              selected={orgIds}
              onToggle={(id) => setOrgIds(toggle(orgIds, id))}
            />
            <RowChecks
              legend="Jobs"
              rows={rows.opportunities}
              selected={jobIds}
              onToggle={(id) => setJobIds(toggle(jobIds, id))}
            />
            <RowChecks
              legend="Applications"
              rows={rows.applications}
              selected={applicationIds}
              onToggle={(id) => setApplicationIds(toggle(applicationIds, id))}
            />
          </>
        ) : null}
        <Button type="button" variant="destructive" disabled={selectedCount === 0} onClick={() => void onPreview()}>
          {preview.isPending ? 'Previewing…' : 'Preview removal'}
        </Button>
      </div>
      <PreviewDialog
        open={open}
        preview={plan}
        pending={purge.isPending}
        title="Remove what this run generated?"
        onOpenChange={setOpen}
        onConfirm={() => void onConfirm()}
      />
    </EntityManageSection>
  )
}

export function DataBenchTenantSweep() {
  const preview = usePreviewTenantPurge()
  const purge = usePurgeTenantRows()
  const [open, setOpen] = useState(false)
  const [plan, setPlan] = useState<SimulationPurgePreview | null>(null)

  async function onPreview() {
    try {
      const next = await preview.mutateAsync()
      setPlan(next)
      setOpen(true)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not preview removal')
    }
  }

  async function onConfirm() {
    try {
      await purge.mutateAsync()
      toast.success('Simulation rows removed')
      setOpen(false)
      setPlan(null)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not remove rows')
    }
  }

  return (
    <EntityManageSection
      title="Remove every simulation row"
      subtitle="People, organisations, jobs, and applications marked by a simulation stay until you remove them here. The tenant anchor organisation is kept. Preview does not delete."
    >
      <Button type="button" variant="destructive" onClick={() => void onPreview()}>
        {preview.isPending ? 'Previewing…' : 'Preview tenant removal'}
      </Button>
      <PreviewDialog
        open={open}
        preview={plan}
        pending={purge.isPending}
        title="Remove every simulation-marked row in this tenant?"
        onOpenChange={setOpen}
        onConfirm={() => void onConfirm()}
      />
    </EntityManageSection>
  )
}
